import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, PenTool } from 'lucide-react';

const RecordingControls: React.FC = () => {
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [isPencilHovered, setIsPencilHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 20,
        delay: 0.2
      }}
      className="fixed bottom-0 left-0 right-0 flex justify-center px-8 pb-8 pointer-events-none"
    >
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden pointer-events-auto"
        style={{
          boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.02)'
        }}
      >
        <div className="flex items-stretch h-20">
          {/* Pencil Section - Left Side */}
          <motion.button
            onHoverStart={() => setIsPencilHovered(true)}
            onHoverEnd={() => setIsPencilHovered(false)}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/editor?mode=edit')}
            className="relative px-8 flex items-center justify-center group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <motion.div
              animate={{
                rotate: isPencilHovered ? [0, -10, 10, -10, 0] : 0,
              }}
              transition={{
                duration: 0.5,
                ease: "easeInOut"
              }}
            >
              <PenTool 
                className="w-7 h-7 text-gray-600 relative z-10" 
                strokeWidth={1.5}
              />
            </motion.div>
          </motion.button>

          {/* Elegant Separator */}
          <div className="w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent self-stretch my-4" />

          {/* Microphone Section - Right Side, Takes More Space */}
          <motion.button
            onClick={() => {
              setIsRecording(true);
              navigate('/editor?mode=record');
            }}
            whileTap={{ scale: 0.97 }}
            className="relative px-16 flex items-center justify-center group overflow-hidden"
          >
            {/* Animated Background Gradient */}
            <motion.div
              className="absolute inset-0"
              animate={{
                background: isRecording 
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
                  : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'
              }}
              transition={{ duration: 0.3 }}
            />
            
            {/* Pulse Effect when Recording */}
            <AnimatePresence>
              {isRecording && (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ 
                    scale: [1, 1.5, 1], 
                    opacity: [0.5, 0, 0.5] 
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute inset-0 bg-red-400"
                />
              )}
            </AnimatePresence>

            {/* Icon Container with Subtle Animation */}
            <motion.div
              className="relative z-10"
              animate={{
                scale: isRecording ? [1, 1.1, 1] : 1,
              }}
              transition={{
                duration: 1,
                repeat: isRecording ? Infinity : 0,
                ease: "easeInOut"
              }}
            >
              <Mic 
                className="w-8 h-8 text-white" 
                strokeWidth={1.5}
                fill={isRecording ? 'currentColor' : 'none'}
              />
            </motion.div>

            {/* Subtle Shine Effect */}
            <motion.div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: 'linear-gradient(105deg, transparent 40%, rgba(255, 255, 255, 0.1) 50%, transparent 60%)'
              }}
              animate={{
                x: [-100, 200]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatDelay: 3,
                ease: "easeInOut"
              }}
            />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default RecordingControls;